// Hive groups — fetched from Supabase circles + beehive data
import { getEffectiveTranslations } from './i18n';
import { getNodeById } from './categoryTree';

function t(lang, key) { return getEffectiveTranslations(lang||'en')[key]||key; }

// ── BeeLove Hive Groups — Configurable community groupings ───────────────
//
// PRIVACY DESIGN PRINCIPLES:
// 1. Groups are based on COMPATIBILITY signals, not individual attributes
// 2. We never expose *why* someone is in a group ("based on your lifestyle")
//    — only that they share "affinities" with others
// 3. Profile fields used for grouping are never displayed as the grouping reason
// 4. Users with hidden fields are still eligible for groups (privacy setting
//    hides fields from other profiles, not from community matching)
// 5. Admin can configure which signals each group uses

// ── GROUP TYPES ───────────────────────────────────────────────────────────
// interest  — based on interests[] tags (public, user-chosen)
// vibe      — based on mood, journey stage, quiz answers (private signals, aggregate display)
// lifestyle — based on lifestyle fields (diet, exercise, etc.) — aggregate only
// value     — based on values fields (religion, politics) — very careful, opt-in feel
// compat    — based on quiz compatibility score with current user

export const GROUP_SIGNALS = {
  interest:  { label: 'Shared interests',    privacy: 'low',    desc: 'Based on what members list as interests' },
  vibe:      { label: 'Community vibe',       privacy: 'low',    desc: 'Based on mood and how members show up' },
  lifestyle: { label: 'Lifestyle affinity',   privacy: 'medium', desc: 'Based on lifestyle — shown only as group affinity, not individual data' },
  value:     { label: 'Shared values',        privacy: 'high',   desc: 'Sensitive — members must have filled these in and set them visible' },
  compat:    { label: 'High compatibility',   privacy: 'low',    desc: 'Based on compatibility quiz scores with current user' },
  geo:       { label: 'Neighbourhood',        privacy: 'low',    desc: 'Based on neighbourhood or area of the city' },
};

// ── DEFAULT GROUP DEFINITIONS ─────────────────────────────────────────────
export function getDefaultHiveGroups(lang) {
  return [
  {
    id: 'active',
    label: t(lang,'hive.group_active_name'),
    emoji: '★',
    color: '#7AAA58',
    desc: 'Members who love to move — hikers, runners, cyclists and outdoor souls',
    signal: 'interest',
    tags: ['Climbing','Hiking','Running','Cycling','Surfing','Skiing','Swimming','Triathlon','Trail running','CrossFit','Gym','Yoga','Sailing','Diving'],
    categoryIds: [
      'interests.sports.outdoor',
      'interests.sports.water',
      'interests.sports.fitness',
      'interests.sports.team',
      'lifestyle.exercise.daily',
      'lifestyle.exercise.3_4',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 0,
  },
  {
    id: 'food',
    label: t(lang,'hive.group_food_name'),
    emoji: '✦',
    color: '#C9A84C',
    desc: t(lang,'hive.group_food_desc'),
    signal: 'interest',
    tags: ['Wine','Coffee','Cooking','Baking','Gastronomy','Craft beer','Natural wine','Tea','BBQ','Pastry','Street food','Sushi'],
    categoryIds: [
      'interests.food.coffee',
      'interests.food.wine',
      'interests.food.cooking',
      'interests.food.foodie',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 1,
  },
  {
    id: 'creative',
    label: t(lang,'hive.group_arts_name'),
    emoji: '✦',
    color: '#A084C4',
    desc: t(lang,'hive.group_arts_desc'),
    signal: 'interest',
    tags: ['Music','Photography','Design','Art','Theatre','Writing','Architecture','Cinema','Vinyl','Dance','Live music','Jazz','Indie rock'],
    categoryIds: [
      'interests.arts_culture.music',
      'interests.arts_culture.visual_arts',
      'interests.arts_culture.cinema',
      'interests.arts_culture.theatre',
      'interests.arts_culture.literature',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 2,
  },
  {
    id: 'mindful',
    label: t(lang,'hive.group_wellness_name'),
    emoji: '✦',
    color: '#9CA880',
    desc: t(lang,'hive.group_wellness_desc'),
    signal: 'interest',
    tags: ['Meditation','Yoga','Ecology','Plants','Gardening','Health','Nutrition','Naturopathy','Herbalism','Forest bathing','Mindfulness'],
    categoryIds: [
      'interests.mind.meditation',
      'interests.mind.wellness',
      'interests.mind.astrology',
      'interests.social.environment',
      'interests.sports.fitness',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 3,
  },
  {
    id: 'intellectual',
    label: t(lang,'hive.group_curious_name'),
    emoji: '✦',
    color: '#7AADCA',
    desc: t(lang,'hive.group_curious_desc'),
    signal: 'interest',
    tags: ['Reading','Philosophy','Science','History','AI','Podcasts','Board games','Chess','Astronomy','Writing','Strategy'],
    categoryIds: [
      'interests.tech.coding',
      'interests.tech.ai',
      'interests.tech.science',
      'interests.tech.gaming',
      'interests.arts_culture.literature',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 4,
  },
  {
    id: 'night_owls',
    label: t(lang,'hive.group_night_name'),
    emoji: '✦',
    color: '#7A7ACC',
    desc: 'Members who thrive after dark — late risers, creatives and night explorers',
    signal: 'lifestyle',
    // Uses sleepSchedule field privately — never shown as reason
    lifestyleField: 'sleepSchedule',
    lifestyleValues: ['night owl', 'late', 'night'],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 5,
    privacyNote: 'Grouped by sleep schedule affinity — individual data is never shown',
  },
  {
    id: 'plant_based',
    label: t(lang,'hive.group_plant_name'),
    emoji: '✦',
    color: '#5BAA7A',
    desc: t(lang,'hive.group_plant_desc'),
    signal: 'lifestyle',
    lifestyleField: 'diet',
    lifestyleValues: ['vegan', 'vegetarian', 'plant-based', 'pescatarian'],
    categoryIds: [
      'lifestyle.diet.vegan',
      'lifestyle.diet.vegetarian',
      'lifestyle.diet.plantbased',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 6,
    privacyNote: 'Grouped by diet affinity — individual choices are never shown',
  },
  {
    id: 'exploring',
    label: t(lang,'hive.group_open_name'),
    emoji: '✦',
    color: '#CA9B5B',
    desc: 'Members in an open, curious phase — actively discovering what they want',
    signal: 'vibe',
    moods: ['exploring', 'ready', 'growing'],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 7,
    privacyNote: 'Based on members\' current Bee mood — only shown at the group level',
  },
  {
    id: 'french_speakers',
    label: t(lang,'hive.group_french_name'),
    emoji: '✦',
    color: '#4A7FC0',
    desc: t(lang,'hive.group_french_desc'),
    signal: 'interest',
    // Uses languages field
    languageMatch: 'French',
    categoryIds: [
      'demographics.language.french',
    ],
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 8,
    privacyNote: 'Based on spoken languages listed on profile',
  },
  {
    id: 'highly_compatible',
    label: t(lang,'hive.group_highcompat_name'),
    emoji: '✦',
    color: '#C9A84C',
    desc: 'Members with strong quiz compatibility with you — personalized for each user',
    signal: 'compat',
    compatThreshold: 7,
    enabled: true,
    visibleInCommunity: true,
    sortOrder: 9,
    privacyNote: 'Shows members with 70%+ compatibility score with you — private, personalized',
  },
  ];
}
export const DEFAULT_HIVE_GROUPS = getDefaultHiveGroups('en');
// ── Group matching function ───────────────────────────────────────────────
export function getUsersForGroup(group, users, currentUser, getCompatibility) {
  // Category-based matching (new primary method)
  // Uses categoryTree node match() functions — handles interests, lifestyle, demographics
  if (group.categoryIds?.length) {
    return users.filter(u =>
      group.categoryIds.some(catId => {
        const node = getNodeById(catId);
        return node?.match ? node.match(u) : false;
      })
    );
  }

  switch (group.signal) {
    case 'interest': {
      if (group.languageMatch) {
        return users.filter(u => (u.languages || []).some(l =>
          l.toLowerCase().includes(group.languageMatch.toLowerCase())
        ));
      }
      return users.filter(u =>
        (u.interests || []).some(i => {
          const normalized = i.replace(/^interest\./i, '').toLowerCase();
          return (group.tags || []).some(t => t.toLowerCase() === normalized);
        })
      );
    }
    case 'lifestyle': {
      return users.filter(u => {
        const val = u[group.lifestyleField];
        if (!val) return false;
        return (group.lifestyleValues || []).some(v =>
          val.toLowerCase().includes(v.toLowerCase())
        );
      });
    }
    case 'vibe': {
      return users.filter(u =>
        (group.moods || []).includes(u.mood)
      );
    }
    case 'compat': {
      if (!getCompatibility || !currentUser) return [];
      return users.filter(u => {
        if (u.id === currentUser.id) return false;
        const c = getCompatibility(u.id);
        return c && c.score >= (group.compatThreshold || 70);
      });
    }
    case 'geo': {
      const myNeighbourhood = currentUser?.neighbourhood;
      if (!myNeighbourhood) return [];
      return users.filter(u => u.neighbourhood === myNeighbourhood);
    }
    default:
      return [];
  }
}

// ── Load groups (from localStorage if admin saved custom config) ──────────
export function loadHiveGroups(lang) {
  try {
    const saved = localStorage.getItem('beeloveHiveGroups');
    if (saved) {
      // Re-translate saved groups with current lang
      const saved_groups = JSON.parse(saved);
      const fresh = getDefaultHiveGroups(lang||'en');
      return saved_groups.map(sg => {
        const fresh_g = fresh.find(fg => fg.id === sg.id);
        return fresh_g ? { ...sg, name: fresh_g.name, desc: fresh_g.desc } : sg;
      });
    }
  } catch {}
  return getDefaultHiveGroups(lang||'en');
}

export function saveHiveGroups(groups) {
  try {
    // Supabase: await db.scouts().insert(...) — localStorage removed
    // // hive UI state
    localStorage.setItem('beeloveHiveGroups', JSON.stringify(groups));
  } catch {}
}
